import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { useFormValidation } from '../hooks/useFormValidation';
import { useApi } from '../hooks/useApi';
import FormErrorBoundary from './FormErrorBoundary';
import LoadingSpinner from './LoadingSpinner';
import FormInput from './FormInput';
import FormTextarea from './FormTextarea';
import FormSelect from './FormSelect';
import KnowledgeBaseFileManager from './KnowledgeBaseFileManager';
import RevisionHistory from './RevisionHistory';
import UpgradePrompt from './UpgradePrompt';
import WebsiteURLManager from './WebsiteURLManager';

// Validation rules for the parrot form
const validationRules = {
  name: {
    required: true,
    minLength: 1,
    maxLength: 100
  },
  first_message: {
    required: true,
    minLength: 1
  },
  system_prompt: {
    required: true,
    minLength: 10
  },
  voice_id: {
    required: true
  },
  temperature: {
    required: true,
    type: 'number',
    min: 0,
    max: 2
  },
  stability: {
    required: true,
    type: 'number',
    min: 0,
    max: 1
  },
  similarity_boost: {
    required: true,
    type: 'number',
    min: 0,
    max: 1
  },
  style: {
    required: true,
    type: 'number',
    min: 0,
    max: 1
  },
  max_tokens: {
    required: true,
    type: 'number',
    min: 1,
    max: 4000
  },
  knowledge_base_url: {
    type: 'url',
    custom: (value) => {
      // Only validate URL if RAG is enabled and URL is provided
      return null; // Will be handled by conditional validation
    }
  }
};

const ParrotFormEnhanced = ({ parrotId = null, onSave = null }) => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showSuccess, showError } = useToast();
  const { execute, loading: apiLoading, error: apiError } = useApi();

  // Initial form values
  const initialValues = {
    name: '',
    first_message: '',
    system_prompt: '',
    voice_id: '',
    temperature: 0.7,
    model_id: 'eleven_turbo_v2_5',
    use_rag: false,
    knowledge_base_url: '',
    knowledge_base_urls: [], // Array of website URLs for knowledge extraction
    stability: 0.5,
    similarity_boost: 0.8,
    style: 0.0,
    use_speaker_boost: true,
    language: 'en', // Default to English
    max_tokens: 1000
  };

  // Form validation hook
  const {
    values: formData,
    errors,
    touched,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    setValue,
    setValues,
    getFieldProps,
    getCheckboxProps,
    getSelectProps
  } = useFormValidation(initialValues, validationRules);

  // Component state
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [playingPreview, setPlayingPreview] = useState(null);
  const [selectedRAGFiles, setSelectedRAGFiles] = useState([]);
  const [loadingRAGFiles, setLoadingRAGFiles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState({
    isOpen: false,
    message: '',
    upgradeOptions: null,
    showTopupOption: false,
    minimumBalance: 0
  });

  // Load voices on component mount
  useEffect(() => {
    fetchVoices();
  }, []);

  // Load parrot data if editing
  useEffect(() => {
    if (parrotId) {
      fetchParrotData();
      fetchParrotRAGFiles();
    }
  }, [parrotId]);

  const fetchVoices = async () => {
    try {
      setLoadingVoices(true);
      await execute(async (api) => {
        const voicesData = await api.get('/api/voices');
        setVoices(voicesData);
        
        // Set first voice as default if none selected and not editing
        if (voicesData.length > 0 && !formData.voice_id && !parrotId) {
          setValue('voice_id', voicesData[0].service_voice_id);
        }
        
        return voicesData;
      }, { showErrorToast: true });
    } catch (error) {
      console.error('Failed to fetch voices:', error);
    } finally {
      setLoadingVoices(false);
    }
  };

  const fetchParrotData = async () => {
    try {
      await execute(async (api) => {
        const parrotData = await api.get(`/api/parrots/${parrotId}`);
        
        const formValues = {
          name: parrotData.name || '',
          first_message: parrotData.first_message || '',
          system_prompt: parrotData.system_prompt || '',
          voice_id: parrotData.voice_id || '',
          temperature: parrotData.temperature || 0.7,
          model_id: parrotData.model_id || 'eleven_turbo_v2_5',
          use_rag: Boolean(parrotData.use_rag),
          knowledge_base_url: parrotData.knowledge_base_url || '',
          knowledge_base_urls: parrotData.knowledge_base_urls || [],
          stability: parrotData.stability || 0.5,
          similarity_boost: parrotData.similarity_boost || 0.8,
          style: parrotData.style || 0.0,
          use_speaker_boost: parrotData.use_speaker_boost !== false,
          language: parrotData.language || 'en', // Ensure language defaults to 'en'
          max_tokens: parrotData.max_tokens || 1000
        };
        
        setValues(formValues);
        
        return parrotData;
      }, { showErrorToast: true });
    } catch (error) {
      console.error('Failed to fetch parrot data:', error);
    }
  };

  const fetchParrotRAGFiles = async () => {
    try {
      setLoadingRAGFiles(true);
      await execute(async (api) => {
        const ragFiles = await api.get(`/api/parrots/${parrotId}/knowledge-base`);
        setSelectedRAGFiles(ragFiles);
        return ragFiles;
      }, { showErrorToast: false }); // Don't show error toast for optional data
    } catch (error) {
      console.error('Failed to fetch parrot RAG files:', error);
    } finally {
      setLoadingRAGFiles(false);
    }
  };

  const handleRAGFileSelectionChange = async (selectedFiles) => {
    if (!parrotId) {
      // For new parrots, just update the local state
      setSelectedRAGFiles(selectedFiles);
      return;
    }

    try {
      await execute(async (api) => {
        const updatedFiles = await api.post(`/api/parrots/${parrotId}/knowledge-base`, {
          rag_file_ids: selectedFiles.map(f => f.id)
        });
        setSelectedRAGFiles(updatedFiles);
        showSuccess('RAG files updated successfully!');
        return updatedFiles;
      });
    } catch (error) {
      console.error('Error updating RAG files:', error);
    }
  };

  const handleNumberChange = (name, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      handleChange(name, numValue);
    }
  };

  const playVoicePreview = async (voiceId, previewFile) => {
    if (playingPreview === voiceId) {
      setPlayingPreview(null);
      return;
    }

    try {
      setPlayingPreview(voiceId);
      const audio = new Audio(`/api/voices/preview/${previewFile}`);
      audio.onended = () => setPlayingPreview(null);
      audio.onerror = () => {
        setPlayingPreview(null);
        showError('Failed to play voice preview');
      };
      await audio.play();
    } catch (error) {
      console.error('Failed to play preview:', error);
      setPlayingPreview(null);
      showError('Failed to play voice preview');
    }
  };

  const onSubmit = async (formValues) => {
    // Additional validation for conditional fields
    if (formValues.use_rag && formValues.knowledge_base_url) {
      try {
        new URL(formValues.knowledge_base_url);
      } catch {
        showError('Please enter a valid knowledge base URL');
        return;
      }
    }

    try {
      setSaving(true);
      
      await execute(async (api) => {
        const url = parrotId ? `/api/parrots/${parrotId}` : '/api/parrots';
        const method = parrotId ? 'put' : 'post';
        
        const savedParrot = await api[method](url, formValues);
        
        const successMessage = parrotId ? 'Parrot updated successfully!' : 'Parrot created successfully!';
        showSuccess(successMessage);
        
        if (onSave) {
          onSave(savedParrot);
        } else {
          // Navigate back to parrots list after a short delay
          setTimeout(() => {
            navigate('/parrots');
          }, 1500);
        }
        
        return savedParrot;
      });
    } catch (error) {
      console.error('Error saving parrot:', error);
      
      // Check if it's a subscription validation error
      if (error.status === 402) {
        setUpgradePrompt({
          isOpen: true,
          message: error.message || 'Subscription required to create parrots',
          upgradeOptions: error.upgrade_options || null,
          showTopupOption: error.topup_required || false,
          minimumBalance: error.minimum_balance || 0
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Voice options for select component
  const voiceOptions = voices.map(voice => ({
    value: voice.service_voice_id,
    label: `${voice.parrotmate_name} (${voice.gender})`
  }));

  const modelOptions = [
    { value: 'eleven_turbo_v2_5', label: 'Eleven Turbo v2.5 (Recommended)' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' }
  ];

  if (apiLoading && !formData.name) {
    return (
      <LoadingSpinner 
        size="lg" 
        text="Loading parrot data..." 
        className="py-12"
      />
    );
  }

  return (
    <FormErrorBoundary formName="parrot configuration">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* API Error Display */}
        {apiError && (
          <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
            {apiError}
          </div>
        )}

        {/* Basic Information */}
        <div className={`rounded-lg p-6 ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Basic Information</h3>
          
          <div className="space-y-4">
            <FormInput
              label="Parrot Name"
              name="name"
              required
              placeholder="e.g., Customer Support Parrot"
              helpText="Choose a descriptive name for your AI voice parrot"
              {...getFieldProps('name')}
            />

            <FormTextarea
              label="First Message"
              name="first_message"
              required
              rows={3}
              placeholder="e.g., Hello! Thank you for calling. How can I help you today?"
              helpText="This will be spoken word-for-word at the beginning of the call"
              {...getFieldProps('first_message')}
            />
          </div>
        </div>

        {/* Voice Selection */}
        <div className={`rounded-lg p-6 ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Voice Selection</h3>
          
          {loadingVoices ? (
            <LoadingSpinner text="Loading voices..." className="py-4" />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {voices.map((voice) => (
                  <div
                    key={voice.service_voice_id}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.voice_id === voice.service_voice_id
                        ? 'border-teal-500 bg-teal-900/20'
                        : theme === 'dark'
                          ? 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                    }`}
                    onClick={() => handleChange('voice_id', voice.service_voice_id)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className={`font-semibold ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>{voice.parrotmate_name}</h4>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          voice.gender === 'Male' 
                            ? theme === 'dark' 
                              ? 'bg-blue-900 text-blue-200' 
                              : 'bg-blue-100 text-blue-800'
                            : theme === 'dark'
                              ? 'bg-pink-900 text-pink-200'
                              : 'bg-pink-100 text-pink-800'
                        }`}>
                          {voice.gender}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          playVoicePreview(voice.service_voice_id, voice.preview_audio_file);
                        }}
                        className={`p-2 rounded-full transition-colors ${
                          playingPreview === voice.service_voice_id
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-teal-600 hover:bg-teal-700'
                        }`}
                        title={playingPreview === voice.service_voice_id ? 'Stop Preview' : 'Play Preview'}
                      >
                        {playingPreview === voice.service_voice_id ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className={`text-sm mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>{voice.short_description}</p>
                    <p className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>{voice.long_description}</p>
                  </div>
                ))}
              </div>
              {errors.voice_id && touched.voice_id && (
                <p className="text-sm text-red-400">{errors.voice_id}</p>
              )}
            </div>
          )}
        </div>

        {/* Voice Configuration */}
        <div className={`rounded-lg p-6 ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Voice Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="stability" className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Stability: {formData.stability}
              </label>
              <input
                type="range"
                id="stability"
                name="stability"
                min="0"
                max="1"
                step="0.1"
                value={formData.stability}
                onChange={(e) => handleNumberChange('stability', e.target.value)}
                onBlur={() => handleBlur('stability')}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className={`flex justify-between text-xs mt-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <span>Variable</span>
                <span>Stable</span>
              </div>
              <p className={`text-xs mt-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Higher values make the voice more consistent but less expressive
              </p>
              {errors.stability && touched.stability && (
                <p className="mt-1 text-sm text-red-400">{errors.stability}</p>
              )}
            </div>

            <div>
              <label htmlFor="similarity_boost" className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Similarity Boost: {formData.similarity_boost}
              </label>
              <input
                type="range"
                id="similarity_boost"
                name="similarity_boost"
                min="0"
                max="1"
                step="0.1"
                value={formData.similarity_boost}
                onChange={(e) => handleNumberChange('similarity_boost', e.target.value)}
                onBlur={() => handleBlur('similarity_boost')}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className={`flex justify-between text-xs mt-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <span>Low</span>
                <span>High</span>
              </div>
              <p className={`text-xs mt-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Enhances similarity to the original voice
              </p>
              {errors.similarity_boost && touched.similarity_boost && (
                <p className="mt-1 text-sm text-red-400">{errors.similarity_boost}</p>
              )}
            </div>

            <div>
              <label htmlFor="style" className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Style: {formData.style}
              </label>
              <input
                type="range"
                id="style"
                name="style"
                min="0"
                max="1"
                step="0.1"
                value={formData.style}
                onChange={(e) => handleNumberChange('style', e.target.value)}
                onBlur={() => handleBlur('style')}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className={`flex justify-between text-xs mt-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <span>Natural</span>
                <span>Exaggerated</span>
              </div>
              <p className={`text-xs mt-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Controls the stylistic expression of the voice
              </p>
              {errors.style && touched.style && (
                <p className="mt-1 text-sm text-red-400">{errors.style}</p>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="use_speaker_boost"
                name="use_speaker_boost"
                {...getCheckboxProps('use_speaker_boost')}
                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-teal-600 focus:ring-teal-500"
              />
              <label htmlFor="use_speaker_boost" className="ml-2 block text-sm text-gray-200">
                Use Speaker Boost
              </label>
              <div className="ml-2">
                <span className="text-xs text-gray-400">Enhances voice clarity</span>
              </div>
            </div>
          </div>
        </div>

        {/* Conversation Configuration */}
        <div className={`rounded-lg p-6 ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Conversation Configuration</h3>
          
          <div className="space-y-6">
            <FormTextarea
              label="System Prompt"
              name="system_prompt"
              required
              rows={6}
              placeholder="You are a helpful customer service representative. Be friendly, professional, and helpful. Always ask how you can assist the caller."
              helpText="Detailed instructions that define your parrot's personality, behavior, and knowledge"
              {...getFieldProps('system_prompt')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="temperature" className="block text-sm font-medium text-gray-300 mb-2 dark:text-gray-600">
                  Temperature: {formData.temperature}
                </label>
                <input
                  type="range"
                  id="temperature"
                  name="temperature"
                  min="0"
                  max="2"
                  step="0.1"
                  value={formData.temperature}
                  onChange={(e) => handleNumberChange('temperature', e.target.value)}
                  onBlur={() => handleBlur('temperature')}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Deterministic</span>
                  <span>Creative</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Controls creativity and randomness in responses
                </p>
                {errors.temperature && touched.temperature && (
                  <p className="mt-1 text-sm text-red-400">{errors.temperature}</p>
                )}
              </div>

              <FormSelect
                label="Language Model"
                name="model_id"
                options={modelOptions}
                helpText="The AI model that powers your parrot's responses"
                {...getSelectProps('model_id')}
              />

              <FormSelect
                label="Language"
                name="language"
                options={languageOptions}
                {...getSelectProps('language')}
              />

              <FormInput
                label="Max Tokens"
                name="max_tokens"
                type="number"
                min="1"
                max="4000"
                helpText="Maximum length of responses (1-4000)"
                {...getFieldProps('max_tokens')}
              />
            </div>
          </div>
        </div>

        {/* Knowledge Base Configuration */}
        <div className={`rounded-lg p-6 ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Knowledge Base</h3>
          
          <div className="space-y-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="use_rag"
                name="use_rag"
                {...getCheckboxProps('use_rag')}
                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-teal-600 focus:ring-teal-500"
              />
              <label htmlFor="use_rag" className="ml-2 block text-sm text-gray-200">
                Enable RAG (Retrieval-Augmented Generation)
              </label>
            </div>
            
            <p className="text-xs text-gray-400">
              RAG allows your parrot to access and reference external knowledge sources for more informed responses.
            </p>

            {formData.use_rag && (
              <div className="space-y-6">
                {/* Website Knowledge Sources */}
                <WebsiteURLManager
                  urls={formData.knowledge_base_urls}
                  onChange={(urls) => handleChange('knowledge_base_urls', urls)}
                  disabled={saving}
                  parrotId={parrotId}
                />

                {/* Legacy single URL field - kept for backward compatibility */}
                <FormInput
                  label="Legacy Knowledge Base URL (Optional)"
                  name="knowledge_base_url"
                  type="url"
                  placeholder="https://example.com/knowledge-base"
                  helpText="Single URL for backward compatibility. Consider using the Website Knowledge Sources above for multiple URLs."
                  {...getFieldProps('knowledge_base_url')}
                />

                <div>
                  <h4 className={`text-lg font-medium mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>Knowledge Base Files</h4>
                  <p className="text-sm text-gray-400 mb-4">
                    Upload and manage files that your parrot can reference during conversations. 
                    Select files from your library to associate with this parrot.
                  </p>
                  
                  {loadingRAGFiles ? (
                    <LoadingSpinner text="Loading RAG files..." className="py-8" />
                  ) : (
                    <KnowledgeBaseFileManager
                      selectedFiles={selectedRAGFiles}
                      onSelectionChange={handleRAGFileSelectionChange}
                      showSelection={true}
                      showUsage={false}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Revision History - Only show when editing existing parrot */}
        {parrotId && (
          <RevisionHistory parrotId={parrotId} />
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-700">
          <button
            type="button"
            onClick={() => navigate('/parrots')}
            className="px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors dark:text-gray-600"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={saving || !isValid}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors dark:text-white"
          >
            {saving && (
              <LoadingSpinner size="sm" className="mr-2" />
            )}
            {saving ? 'Saving...' : (parrotId ? 'Update Parrot' : 'Create Parrot')}
          </button>
        </div>
      </form>

      {/* Upgrade Prompt */}
      <UpgradePrompt
        isOpen={upgradePrompt.isOpen}
        onClose={() => setUpgradePrompt({ ...upgradePrompt, isOpen: false })}
        message={upgradePrompt.message}
        upgradeOptions={upgradePrompt.upgradeOptions}
        showTopupOption={upgradePrompt.showTopupOption}
        minimumBalance={upgradePrompt.minimumBalance}
      />
    </FormErrorBoundary>
  );
};

export default ParrotFormEnhanced;
